import React, { useEffect, useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { Loader2, CheckCircle2, AlertTriangle, ArrowRight } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import { joinOrgByInvite } from "../api/subscriptions";

/**
 * Join — /join?org=<slug>&token=<48 hex>
 *
 * The redemption half of the "Generate Link" invite flow. The link has always
 * been generated and the token stored, but /join was never a route: the SPA
 * catch-all bounced signed-in visitors to /dashboard and signed-out visitors to
 * /login, and in both cases the token was discarded without anyone joining.
 *
 * Signed-out visitors have the invite parked in sessionStorage so it survives
 * the OAuth round-trip, and the login page brings them back here afterwards.
 */
export const PENDING_INVITE_KEY = "paxley:pending-invite";

type State =
  | { kind: "working" }
  | { kind: "joined"; org: string }
  | { kind: "already"; org: string }
  | { kind: "error"; message: string };

export default function Join() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user, loading, login } = useAuth();
  const [state, setState] = useState<State>({ kind: "working" });

  const token = searchParams.get("token") || "";
  const orgSlug = searchParams.get("org") || "";

  useEffect(() => {
    if (loading) return;

    if (!token) {
      setState({ kind: "error", message: "This invitation link is missing its token." });
      return;
    }

    if (!user) {
      // Park the invite and send the visitor through sign-in. sessionStorage
      // survives the OAuth redirect; React Router state does not.
      sessionStorage.setItem(PENDING_INVITE_KEY, `${window.location.pathname}${window.location.search}`);
      navigate("/login", { replace: true });
      return;
    }

    sessionStorage.removeItem(PENDING_INVITE_KEY);
    let cancelled = false;
    joinOrgByInvite(token, orgSlug)
      .then((res) => {
        if (cancelled) return;
        const name = res.org?.name || orgSlug || "your organization";
        setState(res.already_member ? { kind: "already", org: name } : { kind: "joined", org: name });
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        const message =
          err instanceof Error && err.message
            ? err.message
            : "This invitation could not be accepted.";
        setState({ kind: "error", message });
      });
    return () => {
      cancelled = true;
    };
  }, [loading, user, token, orgSlug, navigate]);

  return (
    <div className="min-h-screen bg-bg flex items-center justify-center px-4">
      <div className="w-full max-w-md rounded-2xl border border-border bg-card p-8 text-center">
        {state.kind === "working" && (
          <>
            <Loader2 className="h-8 w-8 animate-spin text-brand mx-auto mb-4" />
            <h1 className="text-lg font-semibold text-text">Accepting your invitation…</h1>
            <p className="text-sm text-muted mt-2">This only takes a moment.</p>
          </>
        )}

        {(state.kind === "joined" || state.kind === "already") && (
          <>
            <CheckCircle2 className="h-8 w-8 text-emerald-500 mx-auto mb-4" />
            <h1 className="text-lg font-semibold text-text">
              {state.kind === "joined" ? `You've joined ${state.org}` : `You're already in ${state.org}`}
            </h1>
            <p className="text-sm text-muted mt-2">
              {state.kind === "joined"
                ? "You now have member access to this workspace."
                : "No changes were made to your access."}
            </p>
            <button className="btn-primary mt-6 inline-flex items-center gap-2" onClick={() => navigate("/dashboard")}>
              Go to dashboard <ArrowRight className="h-4 w-4" />
            </button>
          </>
        )}

        {state.kind === "error" && (
          <>
            <AlertTriangle className="h-8 w-8 text-high mx-auto mb-4" />
            <h1 className="text-lg font-semibold text-text">Invitation not accepted</h1>
            <p className="text-sm text-muted mt-2">{state.message}</p>
            <div className="mt-6 flex items-center justify-center gap-3">
              {!user && (
                <button className="btn-primary" onClick={login}>
                  Sign in
                </button>
              )}
              <Link to="/dashboard" className="btn-ghost text-sm">
                Go to dashboard
              </Link>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
